/*
 * Copyright © HatioLab Inc. All rights reserved.
 *
 * Label command (ZPL/GRF) — 백엔드 헤드리스 *전용*. 클라이언트 캡처는 anti-aliasing
 * / sub-pixel / DPR 차이로 ZPL 1-bit 정확 비트맵과 호환 안 됨. 또한 자동 출고
 * 워크플로우는 클라이언트 없이 트리거되므로 헤드리스 외 옵션 없음.
 *
 * 비용을 줄이는 두 메커니즘:
 *   1) In-flight dedup: 동일 (template, data, orientation, mirror, upsideDown)
 *      요청이 진행 중이면 같은 Promise 를 공유 — 라벨 출력 큐에서 같은 라벨이
 *      동시 N 회 요청되어도 puppeteer evaluate 는 1 회.
 *   2) LRU GRF 캐시: 같은 입력에 대해 TTL 동안 GRF 재사용 — 대량 인쇄에서
 *      같은 라벨 연속 출력 시 puppeteer 호출 0 회.
 */

import { getHeadlessPool } from './headless-pool-for-label.js'
import { headlessModel } from './headless-model.js'

export interface LabelCommandOptions {
  id?: string
  model?: any
  data?: any
  orientation?: string
  mirror?: boolean
  upsideDown?: boolean
  context: any
  draft?: boolean
}

// ── 캐시 / dedup state ─────────────────────────────────────────────────────

const GRF_CACHE_MAX = 200            // 최근 200 개 라벨 유지
const GRF_CACHE_TTL_MS = 60 * 1000   // 60 초 — id-기반 board update 도 1 분 내 반영

interface CacheEntry { value: string; expiresAt: number }
const grfCache = new Map<string, CacheEntry>()
const inFlight = new Map<string, Promise<string | undefined>>()

export function fnv1a(s: string): string {
  let hash = 2166136261
  for (let i = 0; i < s.length; i++) {
    hash ^= s.charCodeAt(i)
    hash = Math.imul(hash, 16777619) >>> 0
  }
  return hash.toString(16)
}

export function cacheKey(opts: LabelCommandOptions): string {
  // id 기반은 board update 가 있을 수 있으므로 TTL 짧게.
  // model 직접 전달은 model 자체 hash — 변경 시 자동으로 새 key.
  const tpl = opts.id
    ? `id:${opts.id}:${opts.draft ? 'd' : 'r'}`
    : `m:${fnv1a(typeof opts.model === 'string' ? opts.model : JSON.stringify(opts.model ?? null))}`
  const dataKey = opts.data ? fnv1a(JSON.stringify(opts.data)) : '_'
  return `${tpl}|${dataKey}|${opts.orientation ?? ''}|${opts.mirror ? 1 : 0}|${opts.upsideDown ? 1 : 0}`
}

function cacheGet(key: string): string | undefined {
  const entry = grfCache.get(key)
  if (!entry) return undefined
  if (entry.expiresAt < Date.now()) {
    grfCache.delete(key)
    return undefined
  }
  // LRU refresh — 최근 사용을 Map 마지막으로 이동.
  grfCache.delete(key)
  grfCache.set(key, entry)
  return entry.value
}

function cacheSet(key: string, value: string): void {
  grfCache.delete(key)
  grfCache.set(key, { value, expiresAt: Date.now() + GRF_CACHE_TTL_MS })
  if (grfCache.size > GRF_CACHE_MAX) {
    const oldest = grfCache.keys().next().value
    if (oldest !== undefined) grfCache.delete(oldest)
  }
}

/** 진단 / 운영용 — 현재 cache hit ratio 등 모니터링 가능. */
export function getLabelCacheStats() {
  return { cacheSize: grfCache.size, inFlight: inFlight.size }
}

/** Test-only — 캐시 / in-flight state 초기화. */
export function _resetLabelState(): void {
  grfCache.clear()
  inFlight.clear()
}

/**
 * Test-only — buildLabelGrf 를 mock 으로 교체. production 에서는 항상 default 사용.
 */
let _buildOverride: ((opts: LabelCommandOptions) => Promise<string | undefined>) | null = null
export function _setBuildOverride(fn: ((opts: LabelCommandOptions) => Promise<string | undefined>) | null): void {
  _buildOverride = fn
}

// ── 헤드리스 evaluate (실제 puppeteer 호출). ──────────────────────────────────

async function buildLabelGrf(opts: LabelCommandOptions): Promise<string | undefined> {
  const { domain } = opts.context.state

  const browser = (await getHeadlessPool().acquire()) as any
  if (!browser) return undefined

  try {
    const { page } = browser

    const { model: resolvedModel } = await headlessModel(
      { domain, model: opts.model, id: opts.id },
      !!opts.draft
    )

    const grf = await page.evaluate(
      async (model, data, orientation, mirror, upsideDown) => {
        //@ts-ignore
        let s = createScene(model)
        if (data) {
          s.data = data
        }
        return new Promise(resolve => {
          // @ts-ignore
          requestAnimationFrame(() => {
            // @ts-ignore
            let grf = imageDataToGrf(s, model, orientation, mirror, upsideDown)
            resolve(grf)
            // @ts-ignore
            sceneContainer.removeChild(s.target)
            s.dispose()
          })
        })
      },
      resolvedModel,
      opts.data,
      opts.orientation,
      opts.mirror,
      opts.upsideDown
    )

    return `
^XA
^GFA,${grf}
^FS
^XZ`
  } finally {
    // 에러 발생 시에도 반드시 풀에 브라우저를 반환
    getHeadlessPool().release(browser)
  }
}

/**
 * 라벨 출력 — ZPL/GRF 문자열 반환.
 *
 * @param {String} id 모델 ID
 * @param {Object} data 매핑할 데이터
 * @param {String} orientation (시계방향) N: 0, R: 90, I: 180, B: 270
 * @param {boolean} mirror 좌우반전
 * @param {boolean} upsideDown 상하반전
 */
export const labelcommand = async (opts: LabelCommandOptions): Promise<string | undefined> => {
  const key = cacheKey(opts)

  // 1) GRF LRU 캐시 — 같은 입력 반복 출력 시 puppeteer 호출 0.
  const cached = cacheGet(key)
  if (cached !== undefined) return cached

  // 2) In-flight dedup — 동일 요청이 이미 진행 중이면 같은 Promise share.
  const existing = inFlight.get(key)
  if (existing) return existing

  const builder = _buildOverride ?? buildLabelGrf
  const promise = builder(opts)
    .then(result => {
      if (result) cacheSet(key, result)
      return result
    })
    .finally(() => {
      inFlight.delete(key)
    })

  inFlight.set(key, promise)
  return promise
}
